import { CLMM_PROGRAM_ID, getPdaPersonalPositionAddress } from "@raydium-io/raydium-sdk-v2";
import { USDV_MINT } from "@shared/tokens/usdv";
import { PublicKey } from "@solana/web3.js";
import type { YaasLpPositionIdentity, YaasPositionCardData } from "@solomon-apps/shared/src/types/yaas";
import { BigDecimal } from "@solomon-labs/bigdecimal";
import { TOKEN_2022_PROGRAM_ID_STRING, TOKEN_PROGRAM_ID_STRING } from "@solomon-labs/constants";
import { ConnectionManager } from "@solomon-labs/solana-connection";

import { isOwnedNft } from "./lpOwnership";
import { requestLpRpc } from "./lpRpc";
import {
    type LpRpc,
    type ParsedTokenAccountsResponse,
    RAYDIUM_CLMM_LABEL,
    type RaydiumLpHolding,
    type RpcAccountsResponse
} from "./lpTypes";
import { decodeRaydiumPool, decodeRaydiumPosition, fetchRaydiumLpBalance } from "./raydiumLpBalance";

/**
 * Temporary direct-NFT discovery. Never scans the whole pool or infers ownership from liquidity.
 * Eligibility is protocol and qualifying mint, as in yaas-api: any Raydium CLMM pool holding USDv.
 * The pool list lives in Jachin's catalog, so this rollback path carries no addresses and no labels.
 */
export async function fetchRaydiumLpHoldings(
    walletAddress: string,
    signal?: AbortSignal,
    rpc: LpRpc = ConnectionManager.getRpcClient()
): Promise<RaydiumLpHolding[]> {
    const tokens = await Promise.all(
        [TOKEN_PROGRAM_ID_STRING, TOKEN_2022_PROGRAM_ID_STRING].map((program) =>
            rpc.request<ParsedTokenAccountsResponse>(
                "getTokenAccountsByOwner",
                [walletAddress, { programId: program }, { encoding: "jsonParsed", commitment: "confirmed" }],
                { signal, timeout: 15_000 }
            )
        )
    );
    const minContextSlot = Math.max(...tokens.map((response) => response.context.slot));
    const mints = [
        ...new Set(
            tokens.flatMap((response) =>
                response.value.flatMap(({ account }) => {
                    const info = account.data.parsed.info;
                    return isOwnedNft(info, walletAddress) ? [info.mint] : [];
                })
            )
        )
    ];
    const candidates = mints.map((nftMint) => ({
        nftMint,
        positionAddress: getPdaPersonalPositionAddress(CLMM_PROGRAM_ID, new PublicKey(nftMint)).publicKey.toBase58()
    }));
    const positions: Array<{ nftMint: string; positionAddress: string; poolAddress: string }> = [];
    // Solana getMultipleAccounts accepts at most 100 accounts per request.
    for (let offset = 0; offset < candidates.length; offset += 100) {
        const batch = candidates.slice(offset, offset + 100);
        const response = await requestLpRpc<RpcAccountsResponse>(
            rpc,
            "getMultipleAccounts",
            [
                batch.map((candidate) => candidate.positionAddress),
                { encoding: "base64", commitment: "confirmed", minContextSlot }
            ],
            { signal, timeout: 15_000 }
        );
        if (response.value.length !== batch.length) throw new Error("Incomplete Raydium account response");
        response.value.forEach((account, index) => {
            if (account?.owner !== CLMM_PROGRAM_ID.toBase58()) return;
            const position = decodeRaydiumPosition(account);
            const candidate = batch[index];
            if (position.nftMint.toBase58() !== candidate.nftMint) throw new Error("Raydium NFT identity mismatch");
            positions.push({ ...candidate, poolAddress: position.poolId.toBase58() });
        });
    }
    const poolAddresses = [...new Set(positions.map(({ poolAddress }) => poolAddress))];
    const usdvPools = new Set<string>();
    for (let offset = 0; offset < poolAddresses.length; offset += 100) {
        const batch = poolAddresses.slice(offset, offset + 100);
        const response = await requestLpRpc<RpcAccountsResponse>(
            rpc,
            "getMultipleAccounts",
            [batch, { encoding: "base64", commitment: "confirmed", minContextSlot }],
            { signal, timeout: 15_000 }
        );
        if (response.value.length !== batch.length) throw new Error("Incomplete Raydium pool response");
        response.value.forEach((account, index) => {
            const pool = decodeRaydiumPool(account);
            if (pool.mintA.toBase58() === USDV_MINT || pool.mintB.toBase58() === USDV_MINT) usdvPools.add(batch[index]);
        });
    }
    const identities: Array<Pick<RaydiumLpHolding, "nftMint" | "positionAddress" | "pool">> = positions
        .filter(({ poolAddress }) => usdvPools.has(poolAddress))
        .map(({ poolAddress, ...candidate }) => ({
            ...candidate,
            pool: {
                protocol: "raydium-clmm",
                poolAddress,
                observedAssetMint: USDV_MINT,
                displayName: RAYDIUM_CLMM_LABEL
            }
        }));
    const holdings: RaydiumLpHolding[] = [];
    // Reuse the principal reader, including its fresh NFT ownership check. Bound RPC fan-out.
    for (let offset = 0; offset < identities.length; offset += 4) {
        const batch = await Promise.all(
            identities.slice(offset, offset + 4).map(async (identity) => {
                const balance = await fetchRaydiumLpBalance(
                    {
                        protocol: identity.pool.protocol,
                        poolAddress: identity.pool.poolAddress,
                        positionAddress: identity.positionAddress,
                        observedAssetMint: identity.pool.observedAssetMint
                    },
                    walletAddress,
                    signal,
                    rpc,
                    minContextSlot
                );
                return balance !== null && new BigDecimal(balance.amount).gt(0)
                    ? {
                          ...identity,
                          key: `${identity.pool.poolAddress}:${identity.positionAddress}`,
                          amount: balance.amount
                      }
                    : null;
            })
        );
        for (const holding of batch) if (holding) holdings.push(holding);
    }
    return holdings.sort((a, b) => a.key.localeCompare(b.key));
}

/** Backend identity wins whether the API supplies the NFT mint or its personal-position PDA. */
export function unmatchedLpHoldings(
    holdings: RaydiumLpHolding[],
    positions: YaasPositionCardData[]
): RaydiumLpHolding[] {
    return holdings.filter(
        (holding) =>
            !positions.some(({ position }) => {
                const identity = position.lpIdentity;
                return identity ? matchesLpHolding(identity, { ...holding.pool, ...holding }) : false;
            })
    );
}

/** Match legacy NFT identities without another RPC call. */
export function matchesLpPosition(
    identity: string,
    holding: Pick<RaydiumLpHolding, "positionAddress" | "nftMint">
): boolean {
    if (identity === holding.positionAddress || identity === holding.nftMint) return true;
    try {
        return (
            getPdaPersonalPositionAddress(CLMM_PROGRAM_ID, new PublicKey(identity)).publicKey.toBase58() ===
            holding.positionAddress
        );
    } catch {
        return false;
    }
}

/** Enrollment matching must include the asset leg, not just the LP account. */
export function matchesLpHolding(
    identity: YaasLpPositionIdentity,
    holding: YaasLpPositionIdentity & { nftMint?: string }
): boolean {
    return (
        identity.protocol === holding.protocol &&
        identity.poolAddress === holding.poolAddress &&
        identity.observedAssetMint === holding.observedAssetMint &&
        // Legacy NFT identities exist only for Raydium CLMM; other protocols match exactly.
        (identity.protocol === "raydium-clmm"
            ? matchesLpPosition(identity.positionAddress, holding)
            : identity.positionAddress === holding.positionAddress)
    );
}
